import React from "react";
import { useTranslation } from "react-i18next";
import {
  CircleArrowDown,
  CircleArrowUp,
  CircleMinus,
  CirclePlus
} from "lucide-react";
import Text from "@/components/ui/text";
import { StatusBadge } from "@/components/ui/status-badge";
import IconContainer from "@/components/ui/icon-container";
import { BulletDashed, Refresh } from "@dreamhub/designsystem/icons";
import { ContractRevenueItem } from "@/pages/contracts/types";
import { DraftRowStatus } from "@/pages/contracts/components/termsDraft/types";
import { BillingFrequency } from "@/pages/settings/pages/products-v2/types";
import { cn } from "@/utils/utils";
import { getEnumTKey } from "@/utils/strings";
import { RevenueItemStatus } from "../types";
import {
  formatDateRange,
  formatDiscountCell,
  formatDateShort,
  getBillingFrequencyLabel,
  revenueItemStatusColors
} from "../consts";
import PricingCell from "./PricingCell";
import TermsChangeDot from "@/pages/contracts/components/updateTerms/TermsChangeDot";
import { TermsColumnChanges } from "@/pages/contracts/components/updateTerms/termsChangeMap";
import { RevenueItemColumn } from "../types";
import TotalCell from "./TotalCell";

const draftStatusClasses: Record<NonNullable<DraftRowStatus>, string> = {
  modified: "bg-badges-natural text-natural",
  deleted: "bg-badges-negative text-error",
  new: "bg-badges-positive text-positive"
};

export const DraftTotalChangeIndicator: React.FC<{
  status: DraftRowStatus;
  total: number | null;
  baselineTotal: number | null | undefined;
}> = ({ status, total, baselineTotal }) => {
  // An unknown total on either side has nothing to compare, so no direction shows.
  const delta =
    status === "modified" && total != null && baselineTotal != null
      ? total - baselineTotal
      : 0;
  const icon =
    status === "new" ? (
      <CirclePlus className="text-positive" />
    ) : status === "deleted" ? (
      <CircleMinus className="text-error" />
    ) : delta > 0 ? (
      <CircleArrowUp className="text-positive" />
    ) : delta < 0 ? (
      <CircleArrowDown className="text-error" />
    ) : null;

  return icon ? (
    <IconContainer size="md" className="flex-shrink-0">
      {icon}
    </IconContainer>
  ) : (
    // Holds the indicator's place so rows without one keep the same total offset.
    <div className="size-6 flex-shrink-0" />
  );
};

// Flags derived from the row's service item. Elapsed periods share the service
// item of their current-period row, so the same values apply to both.
export interface RevenueItemServiceFlags {
  isOneTime: boolean;
  isOneTimeLine: boolean;
  isPricingPerPeriod: boolean;
  isFlexiblePricing: boolean;
  // The months the row's term runs for, resolved against the catalog where the
  // line records no duration of its own — what a per-unit price divides by.
  periodInMonths: number;
  serviceTypeName?: string;
}

interface RevenueItemCellsProps extends RevenueItemServiceFlags {
  item: ContractRevenueItem;
  currency: string;
  showConversion: boolean;
  rate: number | undefined;
  accountCurrencySymbol: string;
  // The sign formatCurrency should print in the cells showing a converted figure beside it.
  currencySignOverride?: string;
  showStatusColumn: boolean;
  isDraft?: boolean;
  draftStatus?: DraftRowStatus;
  // Update terms: which of this row's values the pending update changed.
  columnChanges?: TermsColumnChanges;
  effectiveDate?: string;
  // An added row the server hasn't saved yet; it has no status until it has.
  isSaving?: boolean;
  showNoteColumn: boolean;
  // The Note column's content; elapsed-period rows pass none and keep an empty cell.
  noteCell?: React.ReactNode;
}

// The Service-through-Total cells of a revenue item row, shared by the
// current-period row and its expired-period drilldown rows so both render
// identical columns.
const RevenueItemCells: React.FC<RevenueItemCellsProps> = ({
  item,
  isOneTime,
  isOneTimeLine,
  isPricingPerPeriod,
  isFlexiblePricing,
  periodInMonths,
  serviceTypeName,
  currency,
  showConversion,
  rate,
  accountCurrencySymbol,
  currencySignOverride,
  showStatusColumn,
  isDraft = false,
  draftStatus = null,
  columnChanges,
  effectiveDate,
  isSaving = false,
  showNoteColumn,
  noteCell
}) => {
  const { t } = useTranslation();
  // Status is owned by the backend so this tab and the Services table stay in
  // sync (auto-renew handling and expiry live in one place, not the client).
  const revenueItemStatus = (item.status ??
    RevenueItemStatus.DRAFT) as RevenueItemStatus;
  const isDeleted = draftStatus === "deleted";
  const changeDot = (column: RevenueItemColumn) =>
    columnChanges?.[column] ? (
      <TermsChangeDot
        changes={columnChanges[column]!}
        effectiveDate={effectiveDate}
        currency={currency}
        isDiscountPercentage={item.isDiscountPercentage ?? false}
      />
    ) : null;
  const billsOnce = isOneTimeLine;

  return (
    <>
      <td className="px-2.5 py-4 w-[140px]">
        <div className="flex flex-col gap-1">
          <div
            className={cn(
              "flex items-center gap-1.5",
              // The draft badge sits on the name's line, so the name
              // ellipsises rather than wrapping the badge underneath it.
              isDraft ? "min-w-0" : "flex-wrap"
            )}
          >
            <Text
              variant="body"
              className={cn(
                "leading-tight",
                isDraft ? "truncate" : "break-words",
                isDeleted && "opacity-50"
              )}
              hasTruncatedTooltip={isDraft}
            >
              {item.serviceItemName}
            </Text>
            {draftStatus ? (
              <Text
                variant="smBody"
                className={cn(
                  "rounded-md px-1.5 py-0.5 w-fit shrink-0",
                  draftStatusClasses[draftStatus]
                )}
                data-test={`revenue-item-draft-status-${item.id}`}
              >
                {t(`contractTerms.table.draftStatus.${draftStatus}`)}
              </Text>
            ) : null}
          </div>
          {serviceTypeName ? (
            <Text
              variant="smBodySecondary"
              className={cn("capitalize", isDeleted && "opacity-50")}
            >
              {serviceTypeName}
            </Text>
          ) : null}
        </div>
      </td>
      {showStatusColumn ? (
        <td className="px-2.5 py-4 w-[120px]">
          <StatusBadge
            statusColor={revenueItemStatusColors[revenueItemStatus]}
            isTable
            icon={
              revenueItemStatus === RevenueItemStatus.UPCOMING ? (
                <BulletDashed />
              ) : undefined
            }
            data-test={isSaving ? `revenue-item-saving-${item.id}` : undefined}
          >
            {isSaving
              ? t("contractTerms.table.saving")
              : t(
                  getEnumTKey(
                    "contractTerms.table",
                    "statusValues",
                    RevenueItemStatus,
                    revenueItemStatus
                  )
                )}
          </StatusBadge>
        </td>
      ) : null}
      <td className="px-2.5 py-4 w-[160px]">
        {!isOneTime &&
        (isPricingPerPeriod ||
          item.customBillingFrequency !== BillingFrequency.ONCE) ? (
          <div className="flex flex-col gap-0.5 items-start">
            <div className="flex items-center gap-1.5">
              <Text variant="body">{item.durationDisplay}</Text>
              {!isOneTimeLine && item.autoRenewal && (
                <div className="flex items-center gap-1">
                  <IconContainer size="xs" color="secondary">
                    <Refresh />
                  </IconContainer>
                  <Text variant="smBodySecondary">
                    {t("contractTerms.autoRenew")}
                  </Text>
                </div>
              )}
              {changeDot(RevenueItemColumn.PERIOD)}
            </div>
            {item.customOptOutDate ? (
              <div className="bg-badges-warning rounded-md px-[5px] py-1">
                <Text variant="smBodyWarning">
                  {t("contractTerms.optOutOnDate", {
                    date: formatDateShort(item.customOptOutDate)
                  })}
                </Text>
              </div>
            ) : (
              <Text variant="smBodySecondary">
                {formatDateRange(
                  item.serviceStartDate ?? "",
                  item.serviceEndDate ?? ""
                )}
              </Text>
            )}
          </div>
        ) : null}
      </td>
      {/* Widths come from the table's colgroup, which table-fixed makes authoritative. */}
      <td className="px-2.5 py-4">
        <div className="flex items-center gap-1.5">
          <Text variant="body">{item.quantity}</Text>
          {changeDot(RevenueItemColumn.QUANTITY)}
        </div>
      </td>
      <td className="px-2.5 py-4 w-[140px]">
        <PricingCell
          item={item}
          isPricingPerPeriod={isPricingPerPeriod}
          isFlexiblePricing={isFlexiblePricing}
          periodInMonths={periodInMonths}
          currency={currency}
          showConversion={showConversion}
          rate={rate}
          accountCurrencySymbol={accountCurrencySymbol}
          currencySignOverride={currencySignOverride}
        />
        {changeDot(RevenueItemColumn.PRICING)}
      </td>
      <td className="px-2.5 py-4 w-[140px]">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <Text variant="body">
              {billsOnce
                ? t("contractTerms.once")
                : getBillingFrequencyLabel(item.customBillingFrequency, t)}
            </Text>
            {changeDot(RevenueItemColumn.BILLING)}
          </div>
          <Text variant="smBodySecondary">
            {item.billingStartDate
              ? billsOnce
                ? t("contractTerms.billingOnDate", {
                    date: formatDateShort(item.billingStartDate)
                  })
                : t("contractTerms.billingStartingDate", {
                    date: formatDateShort(item.billingStartDate)
                  })
              : null}
          </Text>
        </div>
      </td>
      <td className="px-2.5 py-4">
        <div className="flex items-center gap-1.5">
          <Text variant="body">
            {formatDiscountCell(
              item.discountAmount,
              item.isDiscountPercentage,
              currency,
              currencySignOverride
            )}
          </Text>
          {changeDot(RevenueItemColumn.DISCOUNT)}
        </div>
      </td>
      <td className="px-2.5 py-4 w-[100px]">
        <div className="flex items-center gap-1.5">
          <TotalCell
            item={item}
            currency={currency}
            showConversion={showConversion}
            rate={rate}
            accountCurrencySymbol={accountCurrencySymbol}
            currencySignOverride={currencySignOverride}
          />
          {changeDot(RevenueItemColumn.TOTAL)}
        </div>
      </td>
      {showNoteColumn ? <td className="px-2.5 py-4">{noteCell}</td> : null}
    </>
  );
};

export default RevenueItemCells;
